import * as THREE from 'three';

/**
 * Нормализует опции текстурирования, устанавливая значения по умолчанию
 */
export const normalizeTextureOptions = (options = {}) => {
    const defaultWallRepeat = 5;

    const wallRepeatLength = options.wallRepeatLength || options.wallRepeatHorizontal || options.wallRepeatX || options.repeatX || defaultWallRepeat;
    const wallRepeatHeight = options.wallRepeatHeight || options.wallRepeatVertical || options.wallRepeatY || options.repeatY || defaultWallRepeat;
    const wallRepeatWidth = options.wallRepeatWidth || wallRepeatLength;

    const floorRepeatLength = options.floorRepeatLength || options.floorRepeatX || Math.max(1, wallRepeatLength);
    const floorRepeatWidth = options.floorRepeatWidth || options.floorRepeatY || Math.max(1, wallRepeatWidth);

    return {
        ...options,
        wallRepeatLength,
        wallRepeatHeight,
        wallRepeatWidth,
        floorRepeatLength,
        floorRepeatWidth,
        swapWallUV: !!options.swapWallUV,
        swapFloorUV: !!options.swapFloorUV,
        saturation: options.saturation ?? 1.3,
        emissiveStrength: options.emissiveStrength ?? 0.1
    };
};

/**
 * Создает вариант текстуры с заданными параметрами повторения
 */
export const createTextureVariant = (baseTexture, repeatX, repeatY) => {
    const variant = baseTexture.clone();
    variant.wrapS = THREE.RepeatWrapping;
    variant.wrapT = THREE.RepeatWrapping;
    variant.flipY = baseTexture.flipY;
    variant.generateMipmaps = baseTexture.generateMipmaps;
    variant.minFilter = baseTexture.minFilter;
    variant.magFilter = baseTexture.magFilter;
    variant.anisotropy = baseTexture.anisotropy;
    variant.repeat.set(Math.max(1, repeatX), Math.max(1, repeatY));
    variant.needsUpdate = true;
    return variant;
};

/**
 * Применяет текстуру к конкретному mesh объекту
 * @param {THREE.Mesh} mesh 
 * @param {THREE.Texture} texture 
 * @param {Object} options 
 * @param {Function} saveMaterialCallback 
 */
export const applyTextureToMesh = (mesh, texture, options = {}, saveMaterialCallback = null) => {
    if (!mesh.material) {
        if (process.env.NODE_ENV === 'development') {
            console.warn('⚠️ Mesh не имеет материала:', mesh.name);
        }
        return;
    }

    if (saveMaterialCallback) {
        saveMaterialCallback(mesh);
    }

    mesh.userData = mesh.userData || {};
    if (!mesh.userData.__baseMaterial) {
        mesh.userData.__baseMaterial = mesh.material;
    }
    mesh.userData.__hasFinishingTexture = true;

    if (options.wallOrientation) {
        adjustWallUVs(mesh, {
            orientation: options.wallOrientation,
            lengthScale: options.wallLengthScale ?? 1,
            widthScale: options.wallWidthScale ?? 1,
            swapUV: options.swapWallUV || false
        });
    }

    // Клонируем оригинальный материал
    const newMaterial = mesh.material.clone();

    newMaterial.map = texture;

    newMaterial.transparent = false;
    newMaterial.side = THREE.DoubleSide;

    const saturation = options.saturation !== undefined ? options.saturation : 1.0;
    if (saturation !== 1.0) {
        newMaterial.color = new THREE.Color(1, 1, 1).multiplyScalar(saturation);
    } else {
        newMaterial.color = new THREE.Color(1, 1, 1);
    }

    const emissiveStrength = options.emissiveStrength || 0.05;
    newMaterial.emissive = new THREE.Color(
        emissiveStrength,
        emissiveStrength,
        emissiveStrength
    );

    newMaterial.needsUpdate = true;

    mesh.material = newMaterial;
};

/**
 * Корректирует UV координаты для стен
 */
export const adjustWallUVs = (mesh, { orientation = 'auto', lengthScale = 1, widthScale = 1, swapUV = false } = {}) => {
    const geometry = mesh.geometry;
    if (!geometry || !geometry.attributes?.uv || !geometry.attributes?.position) {
        return;
    }

    const uvAttribute = geometry.attributes.uv;
    const positionAttribute = geometry.attributes.position;

    geometry.userData = geometry.userData || {};

    if (!geometry.userData.__originalUVs) {
        geometry.userData.__originalUVs = uvAttribute.array.slice();
    } else {
        uvAttribute.array.set(geometry.userData.__originalUVs);
    }

    const originalUVs = geometry.userData.__originalUVs;
    const uvArray = uvAttribute.array;
    const positionArray = positionAttribute.array;

    let maxAbsX = 0;
    let maxAbsZ = 0;
    const useAutoDetection = !orientation || orientation === 'auto';

    if (useAutoDetection) {
        for (let i = 0; i < positionArray.length; i += 3) {
            maxAbsX = Math.max(maxAbsX, Math.abs(positionArray[i]));
            maxAbsZ = Math.max(maxAbsZ, Math.abs(positionArray[i + 2]));
        }
    }

    const epsilon = Math.max(maxAbsX, maxAbsZ) * 0.02;

    for (let vertexIndex = 0; vertexIndex < uvAttribute.count; vertexIndex += 1) {
        const positionIndex = vertexIndex * 3;
        const x = positionArray[positionIndex];
        const z = positionArray[positionIndex + 2];

        let scale;

        if (useAutoDetection) {
            const distanceToLongWall = Math.abs(Math.abs(x) - maxAbsX);
            const distanceToShortWall = Math.abs(Math.abs(z) - maxAbsZ);
            const useLengthScale = distanceToLongWall <= distanceToShortWall + epsilon;
            scale = useLengthScale ? lengthScale : widthScale;
        } else if (orientation === 'length') {
            scale = lengthScale;
        } else if (orientation === 'width') {
            scale = widthScale;
        } else {
            scale = 1;
        }

        const originalU = originalUVs[vertexIndex * 2];
        const originalV = originalUVs[vertexIndex * 2 + 1];

        if (swapUV) {
            uvArray[vertexIndex * 2] = originalU;
            uvArray[vertexIndex * 2 + 1] = originalV * scale;
        } else {
            uvArray[vertexIndex * 2] = originalU * scale;
            uvArray[vertexIndex * 2 + 1] = originalV;
        }
    }

    uvAttribute.needsUpdate = true;
};

/**
 * Определяет ориентацию стены (длинная или короткая)
 */
export function getWallOrientation(mesh) {
    const geometry = mesh.geometry;
    if (!geometry) {
        return null;
    }

    if (!geometry.boundingBox) {
        geometry.computeBoundingBox?.();
    }

    if (!geometry.boundingBox) {
        return null;
    }

    const size = new THREE.Vector3();
    geometry.boundingBox.getSize(size);

    if (size.x === 0 && size.z === 0) {
        return null;
    }

    return size.x >= size.z ? 'length' : 'width';
}

/**
 * Извлекает URL текстуры из объекта отделки
 */
export const getTextureUrlFromFinishing = (finishing) => {
    if (!finishing) {
        return null;
    }

    if (finishing.pictures && Array.isArray(finishing.pictures) && finishing.pictures.length > 1) {
        return finishing.pictures[1];
    }

    if (finishing.image) {
        return finishing.image;
    }

    return null;
};
